/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { IModelConnection } from "@itwin/core-frontend";
import { InstanceKey, KeySet } from "@itwin/presentation-common";
import { PresentationLabelsProvider } from "@itwin/presentation-components";

// ******************** Functions for general APIs and Queries ********************

/**
 * Given an array of instance keys, return an array of element names.
 * This is the way to request multiple element names at once.
 */
export const getElementNameFromInstanceKeys = async (iModel: IModelConnection, keys: InstanceKey[]): Promise<string[]> => {
  const presentationProvider = new PresentationLabelsProvider({ imodel: iModel });
  const elementNames: string[] = await presentationProvider.getLabels(keys);
  return elementNames;
};

/**
 * Given an array of element ids, return an array of instance keys.
 * Instance keys contain classname and element id information, so this is a way to request multiple classnames at once (
 * while keeping classnames synced with their element id).
 */
export const getInstanceKeysFromId = async (iModel: IModelConnection, elementIds: string[]) => {
  const query = `SELECT ECInstanceId, ec_classname(ECClassId, 's:c') FROM bis.SpatialElement WHERE ECInstanceId IN (${elementIds.join(", ")})`;
  const elementAsync = iModel.query(query);
  const instanceKeys: InstanceKey[] = [];
  for await (const e of elementAsync) {
    instanceKeys.push({
      id: e[0],
      className: e[1],
    });
  }
  return instanceKeys;
};

// ******************** Functions for Type Conversions ********************

const convertElementIdToInstanceKey = (iModel: IModelConnection, elementId: string, className: string) => {
  return (
    {
      id: elementId,
      className: className,
    }
  );
};

export const convertElementIdToKeySet = async (iModel: IModelConnection, elementId: string, className: string): Promise<KeySet> => {
  const instanceKey = convertElementIdToInstanceKey(iModel, elementId, className);
  return new KeySet([instanceKey]);
};

export const convertElementsToKeySet = async (iModel: IModelConnection, elems: { id: string, class: string }[]) => {
  let keySet = new KeySet();
  for await (const elem of elems) {
    if (elem.id !== undefined && elem.class !== undefined) {
      const instanceKey = convertElementIdToInstanceKey(iModel, elem.id, elem.class);
      try {
        keySet.add(instanceKey);
      } catch (error) {
       // console.log(error);
      }
    }else {
     // console.log(`id: ${elem.id} class: ${elem.class}`);
    }  
  };

  return keySet;
};
